import React, { useCallback, useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { GoogleGlyph, GoogleStars } from './GoogleRating';
import { RuleDot } from './Decor';
import { GOOGLE_REVIEWS_URL, Review, reviews } from '../data/reviews';
import { SECTION_IDS } from '../utils/scroll';
function ReviewCard({
  review
}: {
  review: Review;
}) {
  const [expanded, setExpanded] = useState(false);
  const hasPreview = Boolean(review.preview);
  const text = expanded || !hasPreview ? review.quote : review.preview;
  return <article className="flex w-full shrink-0 snap-start flex-col rounded-xl2 border border-lavender-200 bg-ivory p-6 shadow-soft transition-[box-shadow,transform] duration-200 ease-premium hover:-translate-y-1 hover:shadow-float sm:w-[calc((100%-1.25rem)/2)] sm:p-7 lg:w-[calc((100%-3rem)/3)]" data-id="element-225" data-mp="309x3escqpg8k">
      <div className="flex items-center justify-between" data-id="element-226" data-mp="3f1xlw4iuul8a">
        <GoogleStars className="h-4 w-4" data-id="element-227" data-mp="3jyxscgjm9pzx" />
        <GoogleGlyph className="h-4 w-4" data-id="element-228" data-mp="3jzz5qro3uokt" />
      </div>

      <blockquote className="mt-4 flex-1 text-[0.95rem] leading-relaxed text-ink/85 sm:mt-5" data-id="element-229" data-mp="2rd6z07xn04pr">
        “{text}”
      </blockquote>

      {hasPreview && <button type="button" onClick={() => setExpanded(v => !v)} className="mt-3.5 self-start text-sm font-medium text-plum-700 underline underline-offset-4 transition-colors duration-200 ease-premium hover:text-plum-900" data-id="element-230" data-mp="2mfnkr80xvrxt">
          {expanded ? 'Ver menos' : 'Leer reseña completa'}
        </button>}

      <footer className="mt-5 flex items-center gap-3 border-t border-lavender-200/80 pt-4 sm:mt-7 sm:pt-5" data-id="element-231" data-mp="353shhvj6icn4">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-pill bg-lavender-50 font-display text-base text-plum-700" data-id="element-232" data-mp="30caive4moo5b">
          {review.author.charAt(0)}
        </span>
        <span className="font-display text-lg text-plum-900" data-id="element-233" data-mp="3stwjjygpy4c0">{review.author}</span>
      </footer>
    </article>;
}
export function Reviews() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const offsets = () => {
    const track = trackRef.current;
    if (!track) return [];
    const cards = Array.from(track.children) as HTMLElement[];
    if (cards.length === 0) return [];
    const base = cards[0].offsetLeft;
    return cards.map(card => card.offsetLeft - base);
  };
  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const cards = Array.from(track.children) as HTMLElement[];
    if (cards.length === 0) return;
    const base = cards[0].offsetLeft;
    let closest = 0;
    let min = Number.POSITIVE_INFINITY;
    cards.forEach((card, index) => {
      const distance = Math.abs(card.offsetLeft - base - track.scrollLeft);
      if (distance < min) {
        min = distance;
        closest = index;
      }
    });
    setActive(closest);
  }, []);
  const goTo = (index: number) => {
    const track = trackRef.current;
    const positions = offsets();
    if (!track || positions.length === 0) return;
    const clamped = Math.min(positions.length - 1, Math.max(0, index));
    track.scrollTo({
      left: positions[clamped],
      behavior: 'smooth'
    });
  };
  return <section id={SECTION_IDS.testimonios} aria-labelledby="testimonios-titulo" className="relative bg-gradient-to-b from-cream to-ivory" data-id="element-234" data-mp="c5jsxjvrw145">
      <div className="mx-auto max-w-content px-5 py-14 sm:px-8 sm:py-20 lg:py-28" data-id="element-235" data-mp="m4sjs3vcczma">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-10" data-id="element-236" data-mp="zvxmxfxx4a1d">
          <div className="max-w-xl" data-id="element-237" data-mp="vuc31amyuvho">
            <p className="eyebrow flex items-center gap-3 text-plum-600" data-id="element-238" data-mp="vxgemt2rgtid">
              <RuleDot className="hidden sm:inline-flex" data-id="element-239" data-mp="19ttsls836cs3" />
              Lo que dicen nuestros clientes
            </p>
            <h2 id="testimonios-titulo" className="mt-4 font-display text-[1.7rem] leading-[1.16] text-plum-900 sm:mt-5 sm:text-4xl lg:text-[2.7rem]" data-id="element-240" data-mp="14wv5htmub63e">
              Construimos negocios. También construimos confianza.
            </h2>
          </div>

          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-analytics-id="google_reviews_rating_card" aria-label="5.0 con 8 reseñas en Google. Abre las reseñas en una pestaña nueva." className="flex items-center gap-4 self-start rounded-xl2 border border-lavender-200 bg-ivory px-5 py-4 shadow-soft transition-[box-shadow,transform,border-color] duration-200 ease-premium hover:-translate-y-[1px] hover:border-lavender-300 hover:shadow-float focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum-700 sm:self-auto" data-id="element-241" data-mp="384c8jckv7tsh">
            <GoogleGlyph className="h-7 w-7" data-id="element-242" data-mp="3441zts5oup9x" />
            <div data-id="element-243" data-mp="344tpkmcltssa">
              <div className="flex items-center gap-2" data-id="element-244" data-mp="26nuumw7bc38z">
                <GoogleStars className="h-4 w-4" data-id="element-245" data-mp="26nctbl5smyu7" />
                <span className="font-display text-xl text-plum-900" data-id="element-246" data-mp="2bm64h6xlydr7">5.0</span>
              </div>
              <p className="mt-0.5 text-xs text-muted" data-id="element-247" data-mp="ss3aoi4yowyg">8 reseñas en Google</p>
            </div>
          </a>
        </div>

        <div className="mt-8 sm:mt-12" data-id="element-248" data-mp="or17xrbu62rq">
          <div ref={trackRef} onScroll={handleScroll} className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 sm:gap-5 lg:gap-6" data-id="element-249" data-mp="oo5cqardhs6z">
            {reviews.map(review => <ReviewCard key={review.id} review={review} data-id="element-250" data-mp="9sz4ri8pvjb0" />)}
          </div>

          <div className="mt-5 flex items-center justify-between gap-4 sm:mt-7" data-id="element-251" data-mp="51h5cymcdqs5">
            <div className="flex items-center gap-2" data-id="element-252" data-mp="10f4yegov8ol">
              {reviews.map((review, index) => <button key={review.id} type="button" aria-current={active === index} aria-label={`Ver reseña de ${review.author}`} onClick={() => goTo(index)} className={`h-2 rounded-pill transition-[width,background-color] duration-200 ease-premium ${active === index ? 'w-7 bg-plum-700' : 'w-2 bg-lavender-200'}`} data-id="element-253" data-mp="3s6ymech15wvv" data-mpi={index} />)}
            </div>

            <div className="hidden gap-3 sm:flex" data-id="element-254" data-mp="2zl09fipyj3p7">
              <button type="button" onClick={() => goTo(active - 1)} aria-label="Ver reseña anterior" className="inline-flex h-11 w-11 items-center justify-center rounded-pill border border-lavender-200 bg-ivory text-plum-800 shadow-hair transition-[background-color,box-shadow,transform,border-color] duration-200 ease-premium hover:-translate-y-[1px] hover:border-lavender-300 hover:shadow-soft" data-id="element-255" data-mp="2zmbdzkr88fhd">
                <ArrowLeftIcon className="h-4 w-4" data-id="element-256" data-mp="3mj4unr5h11uw" />
              </button>
              <button type="button" onClick={() => goTo(active + 1)} aria-label="Ver siguiente reseña" className="inline-flex h-11 w-11 items-center justify-center rounded-pill border border-lavender-200 bg-ivory text-plum-800 shadow-hair transition-[background-color,box-shadow,transform,border-color] duration-200 ease-premium hover:-translate-y-[1px] hover:border-lavender-300 hover:shadow-soft" data-id="element-257" data-mp="e4ml1kzvte96">
                <ArrowRightIcon className="h-4 w-4" data-id="element-258" data-mp="28c6ssjh0i4u8" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-7 flex flex-col items-start gap-3 border-t border-lavender-200/80 pt-6 sm:mt-9 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:pt-8" data-id="element-259" data-mp="1ko73zp39goia">
          <p className="text-[0.975rem] text-muted" data-id="element-260" data-mp="1ul23obb9suy5">¿Quieres conocer más experiencias?</p>
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-analytics-id="google-reviews-section" className="inline-flex w-full items-center justify-center gap-2.5 rounded-pill border border-lavender-200 bg-ivory px-5 py-3 text-sm font-medium text-plum-800 shadow-hair transition-[background-color,box-shadow,transform,border-color] duration-200 ease-premium hover:-translate-y-[1px] hover:border-lavender-300 hover:shadow-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum-700 sm:w-auto" data-id="element-261" data-mp="1pm8p0fk8yxwe">
            <GoogleGlyph className="h-4 w-4" data-id="element-262" data-mp="1d4em15qzvdrv" />
            Ver todas las reseñas en Google
          </a>
        </div>
      </div>
    </section>;
}